import clsx from 'clsx'
import NextImage from 'next/legacy/image'
import type { FunctionComponent } from 'react'
import { useIsAllowedToFullUse } from '../../../hooks/useIsAllowedToFullUse'
import { useLocalizedSignIn } from '../../../hooks/useLocalizedSignIn'
import { Translation, useTranslate } from '../../contexts/TranslationContext'
import SvgScales from '../../svg/icons/Scales'
import { useActiveDog } from '../../user/ActiveDogContext'
import { useAuth } from '../../user/UserContext'
import { Button } from '../shared/Button'
import { Hidden } from '../shared/Hidden'
import { NotSpecifiedAlert } from '../shared/NotSpecifiedAlert'
import { Rating } from '../shared/Rating'
import { Tag } from '../shared/Tag'
import style from './ProductBanner.module.sass'

type ProductBannerLockedProps = {
	orderIndex: number
}

export const ProductBannerLocked: FunctionComponent<ProductBannerLockedProps> = ({ orderIndex }) => {
	const { dog } = useActiveDog()

	const topIndex = orderIndex <= 3 ? orderIndex : null

	const translate = useTranslate()
	const { signIn } = useLocalizedSignIn()
	const auth = useAuth()
	const notSigned = auth.status !== 'signedIn'
	const isAllowedToFullUse = useIsAllowedToFullUse()

	return (
		<div className={clsx(style.articleWrapper)}>
			<div className={clsx(style.article, style.articleWrapperBlurred)}>
				<div className={style.leftContent}>
					<div className={style.imageWrapper}>
						<NextImage
							src={`/images/bag_mockup_${orderIndex}.jpeg`}
							alt={translate('alternativeText.placeholderProduct')}
							title={translate('placeholder.image.alt')}
							layout="fill"
							objectFit="contain"
						/>
					</div>
					<Hidden breakpoint="mobileDown">
						<div className={style.buttonCompareWrapper}>
							<Button
								type={{ variant: 'naked', small: true }}
								label={translate('compareButton.button.compare')}
								iconBefore={<SvgScales />}
							/>
						</div>
					</Hidden>
				</div>
				<div className={style.body}>
					<h3 className={style.title}>
						<Translation tr="productBanner.locked.title" />
					</h3>
					<p className={style.paragraph}>
						<Translation tr="productBanner.locked.subtitle" />
					</p>
					<div className={style.additionalInfoWrapper}>
						<div className={style.additionalInfoIcons}>
							<div className={style.additionalInfoIcons}>
								<div className={style.mockedIcon} />
								<div className={style.mockedIcon} />
								<div className={style.mockedIcon} />
							</div>
						</div>
						<span className={style.additionalInfoText}>
							<Translation tr="productBanner.locked.subtitle" />
						</span>
					</div>
				</div>
				<div className={style.rightContent}>
					<Rating rateAmount={70} />
					{dog ? (
						<div className={style.guidelineDailyAmountWrapper}>
							<span className={style.titleGuidelineDailyAmount}>
								<Translation tr="productBanner.dailyDose" />
							</span>
							<div className={style.guidelineDailyAmount}>
								<Translation tr="productBanner.locked.amount" />
							</div>
						</div>
					) : (
						<NotSpecifiedAlert color="grey" />
					)}
					<div className={clsx(style.buttonWrapper, style.buttonWrapperInvisible)}>
						<Button type={{ variant: 'bordered' }} label={translate('compareButton.button.compare')} />
					</div>
				</div>
			</div>
			<>
				{topIndex ? (
					<div className={style.orderLock}>
						<Tag color="greenLight" isLarger label={`Top ${topIndex}`} />
					</div>
				) : undefined}
				<div
					className={clsx(
						style.buttonWrapper,
						style.buttonWrapperLock,
						!isAllowedToFullUse && !notSigned && style.buttonWrapperLockDisabled,
					)}
				>
					<Button
						type={{ variant: 'bordered' }}
						disabled={!notSigned}
						label={
							notSigned ? translate('productBanner.locked.button') : translate('productBanner.locked.button.register')
						}
						onClick={() => signIn()}
					/>
				</div>
			</>
		</div>
	)
}
